import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Calendar,
  CheckCircle2,
  Copy,
  Plus,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/v2/Toast";
import { currencyLabel } from "@/lib/currency";
import { useTenantMe } from "@/lib/tenant-me";
import { StepShell, StepBody } from "./_StepShell";
import { StepHero } from "./_StepHero";
import { cn } from "@/lib/utils";

/**
 * Final step. Folded into the OnboardingShell so the rail
 * stays visible — every step including this one feels like part
 * of the same experience.
 *
 * Replaces the old splash-style "summary" with three "what's next"
 * action cards (matches Notion/Linear post-onboarding patterns)
 * + a calmer summary row at the bottom.
 *
 * No StepFooter — Done owns its own "Go to dashboard" CTA inline.
 */
export function DoneStep() {
  const navigate = useNavigate();
  const me = useTenantMe();
  const toast = useToast();

  useEffect(() => {
    toast.success("All set", "Welcome to your workspace.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tenant = me.data?.tenant;
  const planLabel = tenant?.plan
    ? tenant.plan.charAt(0) + tenant.plan.slice(1).toLowerCase()
    : "Starter";
  const bookingPageUrl = tenant?.slug
    ? `${window.location.protocol}//${window.location.host.replace(/^[^.]+\./, "")}/public/${tenant.slug}`
    : null;

  const copyBookingUrl = async () => {
    if (!bookingPageUrl) return;
    try {
      await navigator.clipboard.writeText(bookingPageUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy", "Select the URL and copy it manually.");
    }
  };

  return (
    <StepShell variant="form">
      <div className="flex items-center gap-4">
        <div className="h-12 w-12 rounded-full bg-trend-positive/15 text-trend-positive inline-flex items-center justify-center">
          <CheckCircle2 className="h-7 w-7" />
        </div>
        <StepHero
          title="You're all set"
          subtitle="Your workspace is ready. Pick somewhere to start, or jump straight to the dashboard."
        />
      </div>

      <StepBody>
        <div>
          <p className="text-[13px] font-medium text-foreground mb-3">
            What's next
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            <ActionCard
              icon={<Plus className="h-4 w-4" />}
              title="Add a service"
              description="Set up your first offering and put it on your booking page."
              onClick={() => navigate("/services?new=1", { replace: true })}
            />
            <ActionCard
              icon={<Users className="h-4 w-4" />}
              title="Invite your team"
              description="Bring teammates in so they can manage bookings with you."
              onClick={() => navigate("/workspace/team", { replace: true })}
            />
            <ActionCard
              icon={<Copy className="h-4 w-4" />}
              title="Share your booking page"
              description="Copy the link and paste it on your site or social bio."
              onClick={copyBookingUrl}
              disabled={!bookingPageUrl}
            />
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="px-5 py-3 border-b border-border">
            <p className="text-[12px] font-medium text-muted-foreground tracking-wide uppercase">
              Setup summary
            </p>
          </div>
          <dl className="divide-y divide-border">
            <SummaryRow label="Workspace" value={tenant?.name ?? "—"} />
            <SummaryRow label="Plan" value={planLabel} />
            <SummaryRow
              label="Currency"
              value={tenant?.currency ? currencyLabel(tenant.currency) : "—"}
            />
            <SummaryRow label="Timezone" value={tenant?.timezone ?? "—"} />
          </dl>
        </div>

        <div className="pt-2 flex items-center gap-3">
          <Button
            size="lg"
            onClick={() => navigate("/", { replace: true })}
          >
            Go to dashboard
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          <Button
            variant="ghost"
            size="lg"
            onClick={() => navigate("/calendar", { replace: true })}
          >
            <Calendar className="h-4 w-4 mr-2" />
            View calendar
          </Button>
        </div>
      </StepBody>
    </StepShell>
  );
}

function ActionCard({
  icon,
  title,
  description,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "rounded-xl border bg-card p-4 text-left flex flex-col gap-2 transition-colors h-full",
        disabled
          ? "opacity-60 cursor-not-allowed"
          : "hover:border-primary/40 hover:bg-muted/40 cursor-pointer",
      )}
    >
      <span className="h-7 w-7 rounded-md bg-primary-light/50 text-primary inline-flex items-center justify-center">
        {icon}
      </span>
      <p className="text-[14px] font-semibold tracking-tight">{title}</p>
      <p className="text-[12px] text-muted-foreground leading-relaxed">
        {description}
      </p>
    </button>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-5 py-3">
      <dt className="text-[13px] text-muted-foreground">{label}</dt>
      <dd className="text-[13px] font-medium text-foreground truncate">
        {value}
      </dd>
    </div>
  );
}
